// Klientská sekce (issue #36, screens/23-klient.md, mockupy
// design/„Klientská sekce.dc.html" + „Klientská sekce mobil.dc.html").
//
// Jedna kostra pro 12 URL pod `/klient/` (`body.customer-page`):
//   nativní `aside.sidebar > .client-center-box > ul` vlevo (restyle na kartu
//   se skupinami a odhlášením), `main#content` vpravo. Vše visí na
//   `html.bs-cust-ready` — bez JS zůstane funkční nativní seznam odkazů.
//
// OVĚŘENO NA DEV INSTANCI 2026-07-29 pod testovacím účtem (opravuje specku):
//   • **Mobilní UA render sidebar MÁ** — `/klient/` i podstránky vrací desktop
//     i iPhone 13 UA bajt na bajt stejný `#content-wrapper` (4 691 B) včetně
//     `aside.sidebar`. Spec (§Mobilní odchylky) čekala, že chybí a pilulky se
//     budou muset dodat staticky. Lištu tedy stavíme z reálných odkazů sidebaru
//     a sidebar na mobilu jen schováváme.
//   • **Nastavení účtu není 17 polí ve 3 formulářích, ale 15 ovládacích prvků
//     v JEDNOM `<form id="register-form">`** — změna e-mailu i hesla je uvnitř
//     „Osobních údajů" (`currentPassword` / `password` / `passwordAgain`),
//     e-mail NENÍ readonly a žádné „viz sekce 2" neexistuje. Doručovací adresa
//     má vlastní URL `/klient/dorucovaci-udaje/` (6 polí, mimo sidebar), takže
//     checkbox „Doručovací adresa je jiná" tady není. `billCountryId` má jedinou
//     možnost (Česká republika) a `companyShopping` je defaultně VYPNUTÝ.
//     Karty proto číslujeme podle nativních `.co-box` bloků, ne podle designu.
//   • Testovací účet nemá objednávky ani doklady → `/klient/objednavky/`,
//     4× doklady, hodnocení i diskuze vrací jen `h1` + jednu větu. Prázdné stavy
//     jsou tedy hlavní stav, který na dev instanci reálně vidíme; tabulkové
//     vzory (karta objednávky s náhledy, „Koupit znovu", roky u dokladů) stojí
//     na datech, která tu nejsou — viz níž „ENHANCEMENTY NA DATA".
//
// ENHANCEMENTY NA DATA (zapnou se samy, až data budou):
//   • `enhanceTables()` otaguje libovolnou nativní tabulku (`data-label` z `thead`)
//     → desktop tabulka, mobil řádkové karty. Nezná sloupce dopředu, takže
//     funguje pro doklady, dodací listy i objednávky bez slepých selektorů.
//   • roky u dokladů a stavové pilulky u objednávek se odvozují ze SLOUPCŮ
//     (hlavička matchne datum / stav) — když sloupec není, prvek se nevykreslí.
//   • mapa „název stavu → barva pilulky" je `STATUS_TONE` (konfigurovatelná,
//     ne hardcoded na 4 hodnoty — obchod si stavy pojmenovává v administraci).
//
// Idempotentní (`html.bs-cust-ready`, `data-bs-cust`).

import { icon } from './icon.js';
import { fillAvatar } from './teamPhoto.js';
import { shoptetData } from '../shoptet-hooks.js';
import { CONTACT, phonesOn, reachContact } from './headerTools.js';
import { asChatTrigger } from './supportChat.js';
import { attachPasswordToggle } from './passwordToggle.js';
import { wholesaleCustomer } from './wholesalePrice.js';
import { NBSP } from './money.js';
import { VO } from '../locale/cs/common.js';
import L from '../locale/cs/customerPage.js';
import { bodyClass, page, withPages } from './pages.js';
import { saleLabel } from './campaign.js';

const READY = 'bs-cust-ready';
const BP_SIDEBAR = '(max-width: 1024px)'; // pod $bp-xl sidebar mizí (jako u výpisu)

// ---------------------------------------------------------------------------
// Model navigace — JEDINÉ místo, kde se nativní odkazy sidebaru překládají na
// ikony, skupiny a zkrácené popisky mobilních pilulek. Popisky položek zůstávají
// nativní (jsou z administrace), zkracujeme jen pilulky (spec §Otevřené body).
// ---------------------------------------------------------------------------
const GROUPS = [
  { key: 'ucet', label: L.groupAccount },
  { key: 'nakupy', label: L.groupPurchases },
  { key: 'doklady', label: L.groupDocs },
  { key: 'vyhody', label: L.groupBenefits },
];

// Klíč = cesta podstránky z manifestu shopu (`pages.account*`); stránka, kterou
// shop nemá nebo jejíž slug není ověřený (null), v mapě chybí a její nativní
// odkaz dostane NAV_FALLBACK.
const NAV = navMap([
  [page('account'), { key: 'hub', icon: 'user', group: 'ucet', short: L.navHub }],
  [page('accountSettings'), { key: 'settings', icon: 'cog', group: 'ucet', short: L.navSettings }],
  [page('accountShipping'), { key: 'shipping', icon: 'pin', group: 'ucet', short: L.navShipping }],
  [page('accountOrders'), { key: 'orders', icon: 'package', group: 'nakupy', short: L.navOrders }],
  [page('accountDocs'), { key: 'docs', icon: 'file', group: 'doklady', short: L.navDocs }],
  [page('accountProforma'), { key: 'proforma', icon: 'file', group: 'doklady', short: L.navProforma }],
  [page('accountCredit'), { key: 'credit', icon: 'refresh', group: 'doklady', short: L.navCredit }],
  [page('accountDeliveryNotes'), { key: 'delivery', icon: 'truck', group: 'doklady', short: L.navDelivery }],
  [page('accountPayments'), { key: 'payments', icon: 'card', group: 'doklady', short: L.navPayments }],
  [page('accountDiscounts'), { key: 'discounts', icon: 'percent', group: 'vyhody', short: L.navDiscounts }],
  [page('accountReviews'), { key: 'reviews', icon: 'star', group: 'vyhody', short: L.navReviews }],
  [page('accountThreads'), { key: 'threads', icon: 'chat', group: 'vyhody', short: L.navThreads }],
]);

function navMap(rows) {
  const map = {};
  rows.forEach(function (row) {
    if (row[0]) map[row[0]] = row[1];
  });
  return map;
}

const NAV_FALLBACK = { key: '', icon: 'file', group: 'vyhody', short: '' };

// Popisy dlaždic rozcestníku (design §„Rozcestník"). Titulek dlaždice je nativní
// popisek odkazu, tady je jen druhý řádek.
const TILE_DESC = {
  settings: L.tileSettings,
  shipping: L.tileShipping,
  orders: L.tileOrders,
  docs: L.tileDocs,
  proforma: L.tileProforma,
  credit: L.tileCredit,
  delivery: L.tileDelivery,
  payments: L.tilePayments,
  discounts: L.tileDiscounts,
  reviews: L.tileReviews,
  threads: L.tileThreads,
};

// Perex pod H1 (design má pro každou stránku vlastní větu)
const PEREX = {
  hub: L.perexHub,
  settings: L.perexSettings,
  shipping: L.perexShipping,
  orders: L.perexOrders,
  docs: L.perexDocs,
  proforma: L.perexProforma,
  credit: L.perexCredit,
  delivery: L.perexDelivery,
  payments: L.perexPayments,
  discounts: L.perexDiscounts,
  reviews: L.perexReviews,
  threads: L.perexThreads,
};

// Prázdné stavy. Nativní větu Shoptetu („Ještě jste žádnou objednávku
// nevytvořil…") NEPŘEPISUJEME — je z administrace a jede jako perex panelu.
const EMPTY = {
  orders: { icon: 'package', title: L.emptyOrdersTitle, cta: L.emptyCta, href: '/' },
  docs: { icon: 'file', title: L.emptyDocsTitle },
  proforma: { icon: 'file', title: L.emptyProformaTitle },
  credit: { icon: 'refresh', title: L.emptyCreditTitle },
  delivery: { icon: 'truck', title: L.emptyDeliveryTitle },
  payments: { icon: 'card', title: L.emptyPaymentsTitle },
  reviews: { icon: 'star', title: L.emptyReviewsTitle },
  threads: { icon: 'chat', title: L.emptyThreadsTitle },
};

// Zkratky pod prázdným stavem (URL ověřené na dev instanci — všechny 200)
const SHORTCUTS = withPages([
  { href: page('sale'), label: L.shortcutSale, icon: 'percent' },
  { href: page('clearance'), label: L.shortcutClearance, icon: 'bolt' },
  { href: page('brands'), label: L.shortcutBrands, icon: 'store' },
]);

// Mapa „název stavu → barva pilulky". Klíč = podřetězec nativního názvu stavu
// (obchod si stavy pojmenovává v administraci) → tón. Doplnit dle reálného
// číselníku obchodu, ne přepisovat na pevné čtyři hodnoty (spec §Shoptet mapování).
const STATUS_TONE = [
  { re: L.toneOffRe, tone: 'off', icon: 'close' },
  { re: L.toneOkRe, tone: 'ok', icon: 'check' },
  { re: L.toneShipRe, tone: 'ship', icon: 'truck' },
  { re: L.toneWorkRe, tone: 'work', icon: 'clock' },
];

const LABEL_EYEBROW = L.eyebrow;
const LABEL_SUPPORT_TITLE = L.supportTitle;
const LABEL_SUPPORT_TEXT = L.supportText;
const LABEL_SUPPORT_TEXT_NO_PHONE = L.supportTextNoPhone;
const LABEL_SAVED = L.saved;
const LABEL_EDIT = L.edit;
const LABEL_LEVEL = L.level;
const LABEL_LEVEL_CTA = L.levelCta;
const LABEL_B2B_TITLE = L.b2bTitle;
const LABEL_B2B_TEXT = L.b2bText;
const LABEL_B2B_CTA = L.b2bCta;
const LABEL_ALL = L.all;

// ---------------------------------------------------------------------------
// Helpery
// ---------------------------------------------------------------------------
function el(tag, className, text) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

function link(href, className, text) {
  const n = el('a', className, text);
  n.href = href;
  return n;
}

function txt(n) {
  return n ? (n.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

// Text buňky (nebo řádku) tabulky BEZ nativního mobilního popisku. Shoptet do
// každé `td` tabulek objednávek vkládá `span.client-table-heading` („Kód",
// „Stav objednávky"…), který jeho CSS ukazuje jen na mobilu; v blank módu je
// vidět všude a lepil se k hodnotě („Stav objednávky Stornována"). Popisky pro
// mobilní karty bereme z `thead` (`data-label`), nativní span schová CSS.
function cellText(node) {
  if (!node) return '';
  if (!node.querySelector('.client-table-heading')) return txt(node);
  const copy = node.cloneNode(true);
  copy.querySelectorAll('.client-table-heading').forEach(function (h) {
    h.remove();
  });
  return txt(copy);
}

function list(sel, root) {
  return Array.prototype.slice.call((root || document).querySelectorAll(sel));
}

// Shoptet dává každé stránce `body.in-<poslední segment cesty>` — mapu tedy
// odvodíme rovnou z NAV, ať se klíče nemůžou rozejít. Body třída jde první:
// funguje i v offline fixtures (`file://`), kde `pathname` míří na disk.
function classKey() {
  const classes = document.body.className.split(/\s+/);
  for (let i = 0; i < classes.length; i += 1) {
    if (classes[i].indexOf('in-') !== 0) continue;
    const slug = classes[i].slice(3);
    const paths = Object.keys(NAV);
    for (let j = 0; j < paths.length; j += 1) {
      const parts = paths[j].split('/').filter(Boolean);
      if ((parts[parts.length - 1] || 'klient') === slug) return NAV[paths[j]].key;
    }
  }
  return '';
}

function pathKey() {
  const byClass = classKey();
  if (byClass) return byClass;
  const hit = NAV[window.location.pathname.replace(/[?#].*$/, '')];
  return hit ? hit.key : '';
}

// ---------------------------------------------------------------------------
// Identita zákazníka — dataLayer (`shoptet.customer`) je jediný zdroj, který
// je na VŠECH podstránkách. `fullName` bývá null (Shoptet ho plní až z profilu),
// pak dotáhneme jméno z pole `#billFullName` na stránce nastavení.
// ---------------------------------------------------------------------------
function readCustomer() {
  const dl = shoptetData('customer') || (window.shoptet && window.shoptet.customer) || {};
  const field = document.getElementById('billFullName');
  const name = (dl.fullName || (field && field.value) || '').trim();
  const email = (dl.email || '').trim();
  const ratio = typeof dl.priceRatio === 'number' ? dl.priceRatio : 1;
  return {
    name: name,
    email: email,
    ratio: ratio,
    // Zvýhodněný ceník = jiná skupina nebo jiný ceník než výchozí. Název skupiny
    // Shoptet mimo `/klient/klient-slevy/` nikde nevystavuje → do badge píšeme
    // procenta z `priceRatio`, ne vymyšlený název hladiny.
    special: ratio < 1 || (dl.groupId && dl.groupId !== 1) || (dl.priceListId && dl.priceListId !== 1),
  };
}

function initials(cust) {
  const src = cust.name || cust.email;
  if (!src) return '?';
  if (cust.name) {
    const parts = cust.name.split(/\s+/).filter(Boolean);
    return (parts[0].charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : '')).toUpperCase();
  }
  return src.charAt(0).toUpperCase();
}

function firstName(cust) {
  return cust.name ? cust.name.split(/\s+/)[0] : '';
}

// ---------------------------------------------------------------------------
// Sidebar — restyle nativního `.client-center-box`
// ---------------------------------------------------------------------------
function readNav(box) {
  return list(':scope > ul > li', box)
    .filter(function (li) {
      return !li.classList.contains('logout') && li.querySelector('a');
    })
    .map(function (li) {
      const a = li.querySelector('a');
      const href = (a.getAttribute('href') || '').replace(/[?#].*$/, '');
      const def = NAV[href] || NAV_FALLBACK;
      return {
        li: li,
        a: a,
        href: href,
        label: txt(a),
        key: def.key,
        icon: def.icon,
        group: def.group,
        short: def.short || txt(a),
        active: li.classList.contains('active'),
      };
    });
}

function buildSidebar(aside, box, items, cust) {
  const card = el('div', 'bs-cust__card');

  // hlavička s identitou
  const head = el('div', 'bs-cust__id');
  head.appendChild(el('span', 'bs-cust__eyebrow', LABEL_EYEBROW));
  const row = el('div', 'bs-cust__idrow');
  row.appendChild(el('span', 'bs-cust__avatar', initials(cust)));
  const who = el('span', 'bs-cust__who');
  who.appendChild(el('span', 'bs-cust__name', cust.name || cust.email || LABEL_EYEBROW));
  if (cust.name && cust.email) who.appendChild(el('span', 'bs-cust__mail', cust.email));
  row.appendChild(who);
  head.appendChild(row);
  if (cust.special) {
    const badge = el('span', 'bs-cust__badge');
    badge.appendChild(icon('store'));
    // velkoobchodní ceník se známou úrovní: „Ceník Jipos Premium" (bez procenta —
    // skutečná sleva bývá kvůli podlaze marže nižší; mock 2026-09-14)
    const wholesale = wholesaleCustomer(shoptetData('customer'));
    const special = cust.ratio < 1 ? L.specialDiscount(Math.round((1 - cust.ratio) * 100)) : L.specialPlain;
    badge.appendChild(el('span', null, wholesale && wholesale.name ? VO.chip + wholesale.name : special));
    head.appendChild(badge);
  }
  card.appendChild(head);

  // navigace — nativní `ul` zůstává, jen dostane ikony a skupinové labely
  const ul = box.querySelector(':scope > ul');
  ul.classList.add('bs-cust__nav');
  items.forEach(function (item) {
    item.li.className = 'bs-cust__item' + (item.active ? ' is-active' : '');
    item.a.className = 'bs-cust__link';
    if (!item.a.querySelector('.bs-icon')) {
      const label = el('span', 'bs-cust__label', item.label);
      item.a.textContent = '';
      item.a.appendChild(icon(item.icon));
      item.a.appendChild(label);
    }
  });
  // skupinové labely = `<li>` (span přímo v `ul` by byl nevalidní markup)
  GROUPS.forEach(function (g) {
    const first = items.filter(function (i) {
      return i.group === g.key;
    })[0];
    if (!first) return;
    const head2 = el('li', 'bs-cust__group', g.label);
    head2.setAttribute('aria-hidden', 'true');
    ul.insertBefore(head2, first.li);
  });
  card.appendChild(ul);

  box.className = 'client-center-box bs-cust__box';
  box.appendChild(card);
  aside.classList.add('bs-cust__side');
  return card;
}

// Odhlášení + panel podpory. Desktop: odhlášení jako spodní blok karty, panel
// podpory pod kartou. Mobil (sidebar mizí): obojí na konci obsahu. Prvky se
// STĚHUJÍ, neduplikují se — nativní odkaz `/logout/` zůstává v dokumentu jen jednou.
function buildFoot(box) {
  const logoutBlock = el('div', 'bs-cust__logout-block');
  const logout = box.querySelector('a[data-testid="buttonSignout"], li.logout a');
  if (logout) {
    const li = logout.closest('li');
    logout.className = 'bs-cust__logout';
    logout.insertBefore(icon('lock'), logout.firstChild);
    logoutBlock.appendChild(logout);
    if (li) li.remove();
  }

  const support = el('div', 'bs-cust__support');
  const mark = fillAvatar(el('span', 'bs-cust__support-icon'), 'customer', 38);
  support.appendChild(mark);
  const body = el('span', 'bs-cust__support-body');
  body.appendChild(el('span', 'bs-cust__support-title', LABEL_SUPPORT_TITLE));
  body.appendChild(el('span', 'bs-cust__support-text', phonesOn() ? LABEL_SUPPORT_TEXT : LABEL_SUPPORT_TEXT_NO_PHONE));
  support.appendChild(body);
  // chat → e-mail → telefon (reachContact): se zapnutým chatem otevírá slot
  // chat a e-mail zůstává pod ním; bez chatu nese e-mail (nebo telefon)
  const reach = reachContact();
  const tel = link(reach.href, 'bs-cust__support-tel', reach.text);
  support.appendChild(tel);
  if (reach.chat) {
    asChatTrigger(tel, 'customer');
    support.appendChild(link('mailto:' + CONTACT.email, 'bs-cust__support-mail', CONTACT.email));
  }
  support.appendChild(el('span', 'bs-cust__support-hours', CONTACT.hours));

  return { logout: logoutBlock, support: support, foot: el('div', 'bs-cust__foot') };
}

// Rozmístění podle šířky (design: „Odhlásit se" je na mobilu řádek na konci
// obsahu, ne v hlavičce sidebaru).
function placeFoot(parts, card, box, inner) {
  const toMobile = function () {
    if (parts.foot.parentNode !== inner) inner.appendChild(parts.foot);
    parts.foot.appendChild(parts.logout);
    parts.foot.appendChild(parts.support);
  };
  const toDesktop = function () {
    card.appendChild(parts.logout);
    box.appendChild(parts.support);
    if (parts.foot.parentNode) parts.foot.remove();
  };

  if (!window.matchMedia) {
    toDesktop();
    return;
  }
  const mq = window.matchMedia(BP_SIDEBAR);
  const apply = function () {
    if (mq.matches) toMobile();
    else toDesktop();
  };
  apply();
  if (mq.addEventListener) mq.addEventListener('change', apply);
  else if (mq.addListener) mq.addListener(apply);
}

// ---------------------------------------------------------------------------
// Identita na mobilu (mockup `Klientská sekce mobil.dc.html`, blok rozcestníku).
// Sidebar je pod 1024 px celý skrytý, takže avatar, jméno, e-mail i chip
// zvýhodněného ceníku nebyly na mobilu vidět vůbec — u velkoobchodního účtu je
// přitom chip JEDINÝ signál, že zákazník nakupuje na jiném ceníku (změřeno na
// dev testovacím B2B účtem, DR U7 2026-08-30). Mockup je má jako samostatnou
// kartu pod perexem rozcestníku; prvky se tam stěhují stejným mechanismem jako
// odhlášení a podpora v `placeFoot()` (přesun, ne klon — ať existuje jednou).
// ---------------------------------------------------------------------------
function placeIdentity(inner, key) {
  if (key !== 'hub') return;
  const idHead = document.querySelector('.bs-cust__id');
  const row = idHead && idHead.querySelector('.bs-cust__idrow');
  if (!idHead || !row) return;
  const badge = idHead.querySelector('.bs-cust__badge');
  const card = el('div', 'bs-cust__idcard');

  const toMobile = function () {
    if (card.parentNode !== inner) {
      const anchor = inner.querySelector('.bs-cust__perex') || inner.querySelector('.bs-cust__h1');
      if (anchor) anchor.insertAdjacentElement('afterend', card);
      else inner.insertBefore(card, inner.firstChild);
    }
    card.appendChild(row);
    if (badge) card.appendChild(badge);
  };
  const toDesktop = function () {
    idHead.appendChild(row); // eyebrow zůstává první, pořadí se nemění
    if (badge) idHead.appendChild(badge);
    if (card.parentNode) card.remove();
  };

  if (!window.matchMedia) {
    toDesktop();
    return;
  }
  const mq = window.matchMedia(BP_SIDEBAR);
  const apply = function () {
    if (mq.matches) toMobile();
    else toDesktop();
  };
  apply();
  if (mq.addEventListener) mq.addEventListener('change', apply);
  else if (mq.addListener) mq.addListener(apply);
}

// ---------------------------------------------------------------------------
// Mobilní lišta pilulek (design m23 §„podstránky") — sticky pod breadcrumbem.
// Na rozcestníku se nekreslí, tam navigaci dělají dlaždice.
// ---------------------------------------------------------------------------
function buildPills(items, key) {
  if (key === 'hub') return null;
  const bar = el('nav', 'bs-cust__pills');
  bar.setAttribute('aria-label', LABEL_EYEBROW);
  const track = el('div', 'bs-cust__pills-track');
  items.forEach(function (item) {
    const pill = link(item.href, 'bs-cust__pill' + (item.active ? ' is-active' : ''), null);
    pill.appendChild(icon(item.icon));
    pill.appendChild(el('span', null, item.short));
    if (item.active) pill.setAttribute('aria-current', 'page');
    track.appendChild(pill);
  });
  bar.appendChild(track);
  return bar;
}

// aktivní pilulka doprostřed pásu (design: snap lišta)
function scrollPillIntoView(bar) {
  const active = bar.querySelector('.bs-cust__pill.is-active');
  const track = bar.querySelector('.bs-cust__pills-track');
  if (!active || !track) return;
  track.scrollLeft = Math.max(0, active.offsetLeft - track.offsetLeft - 16);
}

// ---------------------------------------------------------------------------
// Hlavička obsahu
// ---------------------------------------------------------------------------
function buildHead(inner, key, cust) {
  const h1 = inner.querySelector('h1');
  if (!h1) return;
  h1.classList.add('bs-cust__h1');
  let perex = PEREX[key];
  if (key === 'hub') {
    perex = L.greeting(firstName(cust)) + perex;
  }
  if (perex && !inner.querySelector('.bs-cust__perex')) {
    h1.insertAdjacentElement('afterend', el('p', 'bs-cust__perex', perex));
  }
}

// ---------------------------------------------------------------------------
// Prázdné stavy — na dev instanci jediný reálný stav většiny podstránek.
// Nativní hláška (`p` / `h4` bez tabulky) se PŘESOUVÁ do panelu jako perex.
// ---------------------------------------------------------------------------
function nativeMessage(inner) {
  const nodes = list(':scope > p, :scope > h4', inner).filter(function (n) {
    return txt(n).length > 3 && !n.classList.contains('bs-cust__perex');
  });
  return nodes.length === 1 ? nodes[0] : null;
}

function buildEmpty(inner, key) {
  const def = EMPTY[key];
  if (!def) return false;
  if (inner.querySelector('table, form, .bs-cust__empty')) return false;
  const msg = nativeMessage(inner);
  if (!msg) return false;

  const panel = el('div', 'bs-cust__empty');
  const mark = el('span', 'bs-cust__empty-icon');
  mark.appendChild(icon(def.icon));
  panel.appendChild(mark);
  panel.appendChild(el('div', 'bs-cust__empty-title', def.title));

  const text = el('p', 'bs-cust__empty-text', txt(msg).replace(/\.{2,}$/, '.'));
  panel.appendChild(text);
  msg.remove();

  const cta = link(def.href || '/', 'bs-cust__empty-cta', def.cta || L.emptyCta);
  cta.appendChild(icon('arrow-right'));
  panel.appendChild(cta);

  const row = el('div', 'bs-cust__shortcuts');
  SHORTCUTS.forEach(function (s) {
    const chip = link(s.href, 'bs-cust__shortcut');
    chip.appendChild(icon(s.icon));
    // akční nabídka po dobu kampaně pod názvem deštníku („Black Friday")
    chip.appendChild(el('span', null, (s.href === page('sale') && saleLabel()) || s.label));
    row.appendChild(chip);
  });
  panel.appendChild(row);

  inner.appendChild(panel);
  return true;
}

// ---------------------------------------------------------------------------
// Rozcestník `/klient/`
// ---------------------------------------------------------------------------
function buildTiles(inner, items) {
  const grid = el('div', 'bs-cust__tiles');
  items
    .filter(function (i) {
      return i.key !== 'hub';
    })
    .forEach(function (item) {
      const tile = link(item.href, 'bs-cust__tile');
      const mark = el('span', 'bs-cust__tile-icon');
      mark.appendChild(icon(item.icon));
      tile.appendChild(mark);
      tile.appendChild(el('span', 'bs-cust__tile-name', item.label));
      const desc = TILE_DESC[item.key];
      if (desc) tile.appendChild(el('span', 'bs-cust__tile-desc', desc));
      grid.appendChild(tile);
    });
  inner.appendChild(grid);
}

// Karty „Uložené údaje" + „Doručovací adresa". Adresu Shoptet na rozcestníku
// nerenderuje (obsah je jen jedna věta), takže karty nesou to, co reálně máme:
// e-mail účtu z dataLayeru a odkazy na obě editační stránky.
function buildSaved(inner, cust, items) {
  const has = function (key) {
    return items.some(function (i) {
      return i.key === key;
    });
  };
  const row = el('div', 'bs-cust__cards');

  const saved = el('div', 'bs-cust__card2');
  const head = el('div', 'bs-cust__card2-head');
  head.appendChild(el('span', 'bs-cust__card2-title', LABEL_SAVED));
  if (has('settings')) head.appendChild(link(page('accountSettings'), 'bs-cust__card2-edit', LABEL_EDIT));
  saved.appendChild(head);
  const defs = el('div', 'bs-cust__defs');
  const mail = el('span', 'bs-cust__def');
  mail.appendChild(el('span', 'bs-cust__def-label', L.mailLabel));
  mail.appendChild(el('span', 'bs-cust__def-value', cust.email || '–'));
  defs.appendChild(mail);
  if (cust.name) {
    const who = el('span', 'bs-cust__def');
    who.appendChild(el('span', 'bs-cust__def-label', L.nameLabel));
    who.appendChild(el('span', 'bs-cust__def-value', cust.name));
    defs.appendChild(who);
  }
  saved.appendChild(defs);
  row.appendChild(saved);

  // stránku doručovacích údajů shop mít nemusí (SK slug neověřený) → bez karty
  if (!page('accountShipping')) {
    inner.appendChild(row);
    return;
  }
  const ship = link(page('accountShipping'), 'bs-cust__promo');
  const mark = el('span', 'bs-cust__promo-icon');
  mark.appendChild(icon('pin'));
  ship.appendChild(mark);
  const body = el('span', 'bs-cust__promo-body');
  body.appendChild(el('span', 'bs-cust__promo-title', L.shipTitle));
  body.appendChild(el('span', 'bs-cust__promo-text', L.shipText));
  ship.appendChild(body);
  ship.appendChild(icon('arrow-right'));
  row.appendChild(ship);

  inner.appendChild(row);
}

function buildHub(inner, items, cust) {
  if (inner.querySelector('.bs-cust__tiles')) return;
  // nativní hláška „Ještě jste žádnou objednávku nevytvořil…" = poznámka nad
  // dlaždicemi. Zákazník S objednávkami místo ní dostane nativní „Poslední
  // 3 objednávky" (h2 + tabulka) — tu restyluje `enhanceTables()` v initu.
  const msg = nativeMessage(inner);
  if (msg) {
    const note = el('div', 'bs-cust__note');
    note.appendChild(icon('info'));
    note.appendChild(el('span', null, txt(msg).replace(/\.{2,}$/, '.')));
    msg.replaceWith(note);
  }
  buildTiles(inner, items);
  buildSaved(inner, cust, items);
}

// ---------------------------------------------------------------------------
// Tabulky (doklady, dodací listy, objednávky) — generický restyle.
// Sloupce dopředu neznáme, takže popisky pro mobilní řádkové karty bereme
// z `thead`. Bez `thead` (tabulka slev) zůstává dvousloupcový výpis.
// ---------------------------------------------------------------------------
const NUM_RE = L.numRe;
const DATE_RE = L.dateRe;
// „Stav objednávky" (objednávky) i holé „Stav" (doklady) — první shoda vyhrává,
// takže „Stav platby" za ním pilulku nedostane
const STATUS_RE = L.statusColRe;

function toneOf(label) {
  for (let i = 0; i < STATUS_TONE.length; i += 1) {
    if (STATUS_TONE[i].re.test(label)) return STATUS_TONE[i];
  }
  return null;
}

function enhanceTables(inner) {
  return list('table', inner).map(function (table) {
    const heads = list('thead th, thead td', table).map(txt);
    table.classList.add('bs-cust__table');
    // obal kvůli radiusu a vodorovnému scrollu (tabulka `overflow` ignoruje)
    let wrap = table.parentNode;
    if (!wrap || !wrap.classList.contains('bs-cust__tablewrap')) {
      wrap = el('div', 'bs-cust__tablewrap');
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    }
    if (!heads.length) {
      table.classList.add('bs-cust__table--plain');
      return { table: table, wrap: wrap, heads: heads };
    }
    // objednávky mají 7 sloupců („Cena celkem včetně DPH", „Sledování zásilky")
    // a s nezalomenými hlavičkami přetékaly obsah o ~140 px — viz --wide v SCSS
    if (heads.length >= 7) table.classList.add('bs-cust__table--wide');
    // hlavička částky lícuje s hodnotami (ty jsou zarovnané vpravo)
    list('thead th, thead td', table).forEach(function (th, i) {
      if (NUM_RE.test(heads[i])) th.classList.add('bs-cust__th--num');
    });
    const dateCol = heads.findIndex(function (h) {
      return DATE_RE.test(h);
    });
    const statusCol = heads.findIndex(function (h) {
      return STATUS_RE.test(h);
    });
    list('tbody tr', table).forEach(function (tr) {
      list(':scope > td', tr).forEach(function (td, i) {
        if (!heads[i]) return;
        td.setAttribute('data-label', heads[i]);
        if (NUM_RE.test(heads[i])) td.classList.add('bs-cust__td--num');
        if (i === statusCol && !td.querySelector('.bs-cust__chip') && cellText(td)) {
          const label = cellText(td);
          const tone = toneOf(label);
          const chip = el('span', 'bs-cust__chip bs-cust__chip--' + (tone ? tone.tone : 'off'));
          if (tone) chip.appendChild(icon(tone.icon));
          chip.appendChild(el('span', null, label));
          td.textContent = '';
          td.appendChild(chip);
        }
      });
    });
    return { table: table, wrap: wrap, heads: heads, dateCol: dateCol, statusCol: statusCol };
  });
}

// Řádek tabulky skrývají dva nezávislé ovladače (pilulkový filtr a hledání
// čísla objednávky). Každý si drží vlastní důvod a `hidden` je jejich součet —
// dřív jeden přepsal výsledek druhého (code review B5-05).
const HIDE_REASONS = ['filter', 'search'];
function hideRow(tr, reason, off) {
  if (!tr.bsHide) tr.bsHide = {};
  tr.bsHide[reason] = off;
  tr.hidden = HIDE_REASONS.some(function (r) {
    return !!tr.bsHide[r];
  });
}

// Pilulkový filtr nad tabulkou — hodnoty se berou ze SLOUPCE (roky u dokladů,
// stavy u objednávek). Když sloupec chybí nebo je hodnota jen jedna, nekreslí se.
function buildTableFilter(info, kind) {
  if (!info || !info.heads || !info.heads.length) return null;
  const col = kind === 'year' ? info.dateCol : info.statusCol;
  if (col == null || col < 0) return null;

  const rows = list('tbody tr', info.table);
  const valueOf = function (tr) {
    const cell = tr.children[col];
    if (!cell) return '';
    if (kind !== 'year') return cellText(cell);
    const m = cellText(cell).match(/\b(20\d{2})\b/);
    return m ? m[1] : '';
  };

  const values = [];
  rows.forEach(function (tr) {
    const v = valueOf(tr);
    if (v && values.indexOf(v) === -1) values.push(v);
  });
  if (values.length < 2) return null;
  if (kind === 'year') values.sort().reverse();

  const bar = el('div', 'bs-cust__filter');
  const track = el('div', 'bs-cust__filter-track');
  const opts = [LABEL_ALL].concat(values);
  const pills = opts.map(function (v, i) {
    const pill = el('button', 'bs-cust__fpill' + (i === 0 ? ' is-on' : ''), v);
    pill.type = 'button';
    pill.addEventListener('click', function () {
      pills.forEach(function (p) {
        p.classList.remove('is-on');
      });
      pill.classList.add('is-on');
      rows.forEach(function (tr) {
        hideRow(tr, 'filter', i > 0 && valueOf(tr) !== v);
      });
    });
    track.appendChild(pill);
    return pill;
  });
  bar.appendChild(track);
  bar.appendChild(el('span', 'bs-cust__filter-count', L.recordsCount(rows.length)));
  info.wrap.parentNode.insertBefore(bar, info.wrap);
  return bar;
}

// Vyhledávání čísla objednávky (design §Objednávky). Filtruje NAČTENOU stranu —
// při více stranách je to zúžení výpisu, ne serverové hledání.
function buildOrderSearch(info) {
  if (!info || !info.table) return null;
  const rows = list('tbody tr', info.table);
  if (rows.length < 2) return null;

  const wrap = el('div', 'bs-cust__osearch');
  wrap.appendChild(icon('search'));
  const input = el('input', 'bs-cust__osearch-input');
  input.type = 'search';
  input.placeholder = L.orderSearchPlaceholder;
  input.setAttribute('aria-label', L.orderSearchAria);
  input.addEventListener('input', function () {
    const q = input.value.trim().toLowerCase();
    rows.forEach(function (tr) {
      hideRow(tr, 'search', !!q && cellText(tr).toLowerCase().indexOf(q) === -1);
    });
  });
  wrap.appendChild(input);
  info.wrap.parentNode.insertBefore(wrap, info.wrap);
  return wrap;
}

// ---------------------------------------------------------------------------
// Slevy — panel cenové hladiny nad nativní tabulkou `tableCustomerDiscounts`
// (řádky Zákaznická skupina / Věrnostní / Dodatečná / Aktuální sleva).
// ---------------------------------------------------------------------------
function readDiscountRows(table) {
  const out = {};
  list('tbody tr', table).forEach(function (tr) {
    const cells = list(':scope > td', tr);
    if (cells.length < 2) return;
    out[txt(cells[0]).toLowerCase()] = txt(cells[1]);
  });
  return out;
}

function buildLevel(inner, cust) {
  const table = inner.querySelector('[data-testid="tableCustomerDiscounts"], table');
  if (!table || inner.querySelector('.bs-cust__level')) return;
  const rows = readDiscountRows(table);
  let group = '';
  let value = '';
  Object.keys(rows).forEach(function (k) {
    if (L.discountGroupRe.test(k) && !group) group = rows[k];
    if (L.discountValueRe.test(k) && !value) value = rows[k];
  });
  if (!group && !value) return;

  // Velkoobchodní ceník (VO1–VO3) NENÍ procentní sleva: `priceRatio` i všechny
  // řádky tabulky jsou 0 %, zvýhodnění nesou ceny samotného ceníku. „0 % z
  // ceníkových cen" pod názvem „Velkoobchodní odběratel" pak tvrdilo, že
  // zákazník žádnou výhodu nemá (ostrá 2026-09-13). Od 2026-09-14 (mock
  // schválený Tomášem) panel nese úroveň ceníku jako nadpis, skupinu Shoptetu
  // pod ním a slevu úrovně („20 % sleva z aktuálních cen", popis úrovně
  // z `PRICELIST_NAMES`, ne cena produktu); pod panelem pravidla výpočtu.
  const wholesale = wholesaleCustomer(shoptetData('customer'));
  const level = wholesale && wholesale.name && wholesale.discountPct ? wholesale : null;

  const panel = el('div', 'bs-cust__level');
  const left = el('div', 'bs-cust__level-main');
  left.appendChild(el('span', 'bs-cust__level-eyebrow', LABEL_LEVEL));
  left.appendChild(el('span', 'bs-cust__level-name', level ? level.name : group || '–'));
  if (level && group) left.appendChild(el('span', 'bs-cust__level-sub', group));
  panel.appendChild(left);
  const zero = value && parseFloat(value.replace(',', '.')) === 0;
  if (level) {
    const val = el('div', 'bs-cust__level-value');
    val.appendChild(el('span', 'bs-cust__level-num', level.discountPct + NBSP + '%'));
    val.appendChild(el('span', 'bs-cust__level-note', VO.levelValueNote));
    panel.appendChild(val);
  } else if (value && !(zero && cust && cust.special)) {
    const val = el('div', 'bs-cust__level-value');
    val.appendChild(el('span', 'bs-cust__level-num', value));
    val.appendChild(el('span', 'bs-cust__level-note', L.fromPricelist));
    panel.appendChild(val);
  }
  const cta = link(page('contact'), 'bs-cust__level-cta', LABEL_LEVEL_CTA);
  cta.appendChild(icon('arrow-right'));
  panel.appendChild(cta);

  const host = table.closest('.bs-cust__tablewrap') || table;
  host.parentNode.insertBefore(panel, host);
  if (!wholesale) return;

  host.parentNode.insertBefore(buildRules(), host);
  // Nativní tabulka u VO zákazníka hlásí všude 0 % a kurzíva Shoptetu „Konečná
  // výše slevy se může … lišit" pod ní — vedle „20 %" by si protiřečily, obojí
  // nahrazují pravidla. Kdyby Shoptet u VO zákazníka nesl nenulovou slevu
  // (věrnostní, dodatečná), tabulka zůstává.
  const pcts = Object.keys(rows)
    .map(function (k) {
      return rows[k];
    })
    .filter(function (v) {
      return /%/.test(v);
    });
  const allZero = pcts.length > 0 && pcts.every(function (v) {
    return parseFloat(v.replace(',', '.')) === 0;
  });
  if (!allZero) return;
  host.classList.add('bs-cust__native-hidden');
  const note = host.nextElementSibling;
  if (note && note.tagName === 'P' && note.children.length === 1 && note.firstElementChild.tagName === 'EM') {
    note.classList.add('bs-cust__native-hidden');
  }
}

// „Jak se sleva počítá" pod panelem cenové hladiny velkoobchodu (texty cs.js).
const RULE_ICONS = ['percent', 'scale', 'tag'];
function buildRules() {
  const box = el('div', 'bs-cust__rules');
  box.appendChild(el('h2', 'bs-cust__rules-title', VO.rulesTitle));
  const ul = el('ul', 'bs-cust__rules-list');
  VO.rules.forEach(function (text, i) {
    const li = el('li', 'bs-cust__rules-item');
    li.appendChild(icon(RULE_ICONS[i] || 'check'));
    li.appendChild(el('span', null, text));
    ul.appendChild(li);
  });
  box.appendChild(ul);
  return box;
}

function buildB2b(inner) {
  if (inner.querySelector('.bs-cust__b2b') || !page('b2b')) return;
  const band = el('div', 'bs-cust__b2b');
  const body = el('span', 'bs-cust__b2b-body');
  body.appendChild(el('span', 'bs-cust__b2b-title', LABEL_B2B_TITLE));
  body.appendChild(el('span', 'bs-cust__b2b-text', LABEL_B2B_TEXT));
  band.appendChild(body);
  const cta = link(page('b2b'), 'bs-cust__b2b-cta', LABEL_B2B_CTA);
  cta.appendChild(icon('arrow-right'));
  band.appendChild(cta);
  inner.appendChild(band);
}

// ---------------------------------------------------------------------------
// Nastavení účtu — číslované karty nad nativními bloky sekcí.
// Formulář, pole, pořadí i validace zůstávají Shoptetu.
//
// POZOR (DR U7, 2026-08-30): dřív se sekce hledaly jen jako `.co-box` a nadpis
// jako `h4`. Shoptet s příznakem `ums_forms_redesign--on` renderuje nadpisy jako
// `h2` a `.co-box` nese jen 2 ze 4 sekcí — na dev proto vznikaly dvě karty, obě
// pojmenované fallbackem „Údaje" (ověřeno živě). Sekce „Odebírat newsletter"
// (fieldset uvnitř formuláře, nadpis je jeho SOUROZENEC) a „Doručovací adresy"
// (blok za formulářem) `.co-box` nemají vůbec a zůstávaly bez karty.
// Bloky bez `.co-box` proto obalujeme vlastním `div.bs-cust__sec`, aby platil
// stejný kontrakt jako u `.co-box`: karta (rám + radius) > box (mřížka + padding).
// ---------------------------------------------------------------------------
function settingsSections(inner) {
  const out = [];
  const push = function (card, box, title) {
    if (!box || box.closest('.bs-cust__sec-box')) return;
    out.push({ card: card, box: box, title: title || null });
  };

  list('.co-box', inner).forEach(function (co) {
    const box = co.querySelector('fieldset, .box') || co;
    push(co, box, box.querySelector('h2, h3, h4'));
  });

  // „Odebírat newsletter" — nadpis stojí PŘED fieldsetem, ne uvnitř
  const stay = inner.querySelector('fieldset.stay-in-touch');
  if (stay && !stay.closest('.co-box')) {
    const prev = stay.previousElementSibling;
    const h = prev && /^H[234]$/.test(prev.tagName) ? prev : null;
    const card = el('div', null);
    stay.parentNode.insertBefore(card, h || stay);
    card.appendChild(stay);
    push(card, stay, h);
  }

  // „Doručovací adresy" — samostatný blok za formulářem
  const ship = inner.querySelector('.shipping-addresses');
  if (ship && !ship.closest('.co-box')) {
    const card = el('div', null);
    ship.parentNode.insertBefore(card, ship);
    card.appendChild(ship);
    push(card, ship, ship.querySelector('h2, h3, h4'));
  }

  return out;
}

function buildSettings(inner) {
  const sections = settingsSections(inner);
  if (!sections.length || inner.querySelector('.bs-cust__sec-head')) return;

  sections.forEach(function (sec, i) {
    const head = el('div', 'bs-cust__sec-head');
    head.appendChild(el('span', 'bs-cust__sec-num', String(i + 1)));
    const body = el('span', 'bs-cust__sec-titles');
    body.appendChild(el('span', 'bs-cust__sec-title', sec.title ? txt(sec.title) : L.settingsFallback));
    head.appendChild(body);
    if (sec.title) sec.title.remove();
    sec.card.classList.add('bs-cust__sec');
    sec.box.classList.add('bs-cust__sec-box');
    sec.box.insertAdjacentElement('afterbegin', head);
  });

  // dvousloupcová mřížka pro krátká pole (adresa má vlastní poměry v CSS)
  ['billFullName', 'phone', 'email', 'currentPassword', 'password', 'passwordAgain', 'companyId', 'vatId'].forEach(
    function (id) {
      const field = document.getElementById(id);
      const group = field && field.closest('.form-group');
      if (group) group.classList.add('bs-cust__half');
    }
  );

  // hesla: jeden přepínač na skupinu (sdílený passwordToggle.js)
  list('input[type="password"]', inner).forEach(function (input) {
    const group = input.closest('.form-group');
    if (group) {
      group.classList.add('bs-cust__pw');
      attachPasswordToggle(input, group);
    }
  });

  // hlavní CTA
  const submit = inner.querySelector('.submit-wrapper input[type="submit"], .submit-wrapper button[type="submit"]');
  if (submit) {
    submit.classList.add('bs-cust__save');
    const wrap = submit.closest('.submit-wrapper');
    if (wrap) wrap.classList.add('bs-cust__actions');
  }
}

// Doručovací údaje — samostatná URL s jedním nativním `<form>` bez `.co-box`
function buildShipping(inner) {
  const form = inner.querySelector('form');
  if (!form || form.classList.contains('bs-cust__sec-box')) return;
  const fieldset = form.querySelector('fieldset') || form;
  const title = fieldset.querySelector('h4');
  fieldset.classList.add('bs-cust__sec-box');
  const head = el('div', 'bs-cust__sec-head');
  head.appendChild(el('span', 'bs-cust__sec-num', '1'));
  const body = el('span', 'bs-cust__sec-titles');
  body.appendChild(el('span', 'bs-cust__sec-title', title ? txt(title) : L.shippingFallback));
  head.appendChild(body);
  if (title) title.remove();
  fieldset.insertAdjacentElement('afterbegin', head);
  form.classList.add('bs-cust__sec');

  ['deliveryFullName', 'deliveryCompany', 'deliveryCity', 'deliveryZip'].forEach(function (id) {
    const field = document.getElementById(id);
    const group = field && field.closest('.form-group');
    if (group) group.classList.add('bs-cust__half');
  });

  const submit = form.querySelector('input[type="submit"], button[type="submit"]');
  if (submit) {
    submit.classList.add('bs-cust__save');
    const wrap = submit.closest('.submit-wrapper');
    if (wrap) wrap.classList.add('bs-cust__actions');
  }
}

// ---------------------------------------------------------------------------

export function initCustomerPage() {
  const b = document.body.classList;
  if (!b.contains('customer-page')) return;
  // přihlašovací trojice sdílí `body.customer-page` — tu staví authPage.js
  if (['login', 'register', 'forgotPassword'].some((k) => b.contains(bodyClass(k)))) return;

  const wrap = document.querySelector('#content-wrapper .content-wrapper-in');
  const aside = wrap && wrap.querySelector('aside.sidebar');
  const box = aside && aside.querySelector('.client-center-box');
  const content = wrap && wrap.querySelector('main#content');
  const inner = content && content.querySelector('.content-inner');
  // bez přímého `ul` v boxu (jiný markup klientské sekce) zůstane nativní
  // seznam — buildSidebar ho jinak dereferencuje (code review B5-10)
  if (!wrap || !box || !inner || !box.querySelector(':scope > ul') || wrap.dataset.bsCust) return;
  wrap.dataset.bsCust = '1';

  const key = pathKey();
  const cust = readCustomer();
  const items = readNav(box);

  wrap.classList.add('bs-cust');
  content.classList.add('bs-cust__main');

  const card = buildSidebar(aside, box, items, cust);
  placeFoot(buildFoot(box), card, box, inner);

  const pills = buildPills(items, key);
  if (pills) {
    const crumbs = document.querySelector('#content-wrapper .breadcrumbs');
    if (crumbs) crumbs.insertAdjacentElement('afterend', pills);
    else wrap.parentNode.insertBefore(pills, wrap);
    scrollPillIntoView(pills);
  }

  buildHead(inner, key, cust);

  placeIdentity(inner, key);

  if (key === 'hub') {
    // zákazník s objednávkami má na rozcestníku nativní tabulku posledních tří
    enhanceTables(inner);
    buildHub(inner, items, cust);
  }
  else if (key === 'settings') buildSettings(inner);
  else if (key === 'shipping') buildShipping(inner);
  else if (key === 'discounts') {
    enhanceTables(inner);
    buildLevel(inner, cust);
    buildB2b(inner);
  } else if (!buildEmpty(inner, key)) {
    const tables = enhanceTables(inner);
    const info = tables[0];
    if (key === 'orders') {
      buildOrderSearch(info);
      buildTableFilter(info, 'status');
    } else {
      buildTableFilter(info, 'year');
    }
  }

  document.documentElement.classList.add(READY);
}
